import React, {useEffect, useReducer, useState} from 'react';
import ReactDatepicker from 'react-datepicker';
import ReactSelect from 'react-select';

import {Modal} from '../Modal';
import {ActivityButton} from '../ActivityButton';
import {Flow, FlowFetchResponse} from '../../_services/Flow';
import {Choice} from '../../types';
import {ButtonGroup} from '../ButtonGroup';
import {ButtonDropdown} from '../ButtonDropdown';

type AddNewDelegationModalProps = {
    show: boolean;
    hide: () => void;
    save: () => void;
};

type WorkflowStepDelegate = {
    workflow: Workflow;
    step: WorkflowStep;
    email: string;
    type: string;
};

type NewDelegationFormState = {
    startDate: null | Date;
    endDate: null | Date;
    showOutOfOffice: boolean;
    workflowStepDelegates: WorkflowStepDelegate[];
};

const initialFormState: NewDelegationFormState = {
    startDate: null,
    endDate: null,
    showOutOfOffice: false,
    workflowStepDelegates: []
};

type Workflow = {
    id: number;
    title: string;
    steps: WorkflowStep[];
};

type WorkflowStep = {
    id: number;
    name: string;
};

type ModalState = {
    errorMessage: string;
    isLoading: boolean;
    isSaving: boolean;
    showStepDelegation: boolean;
    selectedWorkflow: Choice & Workflow | null;
    selectedWorkflowStep: Choice & WorkflowStep | null;
    workflows: Workflow[];
    addDelegateValue: string;
    selectedDelegateType: Choice | null;
};

type ModalStateAction = {
    type: string;
    payload?: any;
};

const initialModalState: ModalState = {
    errorMessage: '',
    isLoading: true,
    isSaving: false,
    showStepDelegation: false,
    selectedWorkflow: null,
    selectedWorkflowStep: null,
    workflows: [],
    addDelegateValue: '',
    selectedDelegateType: null,
};

type ModalStateReducer = (s: ModalState, a: ModalStateAction) => ModalState;

const modalStateReducer: ModalStateReducer = (state: ModalState, action: ModalStateAction) => {
    switch (action.type) {
        case 'Init':
            return {...initialModalState};
        case 'ErrorMessage':
            return {...state, errorMessage: action.payload};
        case 'StopLoading':
            return {...state, isLoading: false};
        case 'StartSaving':
            return {...state, isSaving: true, errorMessage: ''};
        case 'StopSaving':
            return {...state, isSaving: false};
        case 'SetShowStepDelegation':
            return {...state, showStepDelegation: action.payload};
        case 'SetWorkflows':
            return {...state, workflows: action.payload};
        case 'SelectWorkflow':
            return {...state, selectedWorkflow: action.payload, selectedWorkflowStep: null, addDelegateValue: '', selectedDelegateType: null};
        case 'SelectWorkflowStep':
            return {...state, selectedWorkflowStep: action.payload};
        case 'SetDelegateValue':
            return {...state, addDelegateValue: action.payload};
        case 'ClearSelectedStepAndDelegate':
            return {...state, selectedWorkflowStep: null, addDelegateValue: '', selectedDelegateType: null};
        case 'ClearSelectedWorkflow':
            return {...state, selectedWorkflow: null};
        case 'SetSelectedDelegationType':
            return {...state, selectedDelegateType: action.payload};
        default:
            throw new Error();
    }
};

export const AddNewDelegationModal: React.FunctionComponent<AddNewDelegationModalProps> = ({
                                                                                               show,
                                                                                               hide,
                                                                                               save
                                                                                           }) => {
    const [modalState, dispatch] = useReducer(modalStateReducer, initialModalState);
    const [formState, setFormState] = useState<NewDelegationFormState>(initialFormState);

    useEffect(() => {
        Flow.getInstance().getDelegatableSteps()
            .then((response: FlowFetchResponse) => {
                if (response.ok) {
                    dispatch({type: 'SetWorkflows', payload: response.json.workflows});
                } else {
                    throw Error(response?.json?.message || 'Error loading available steps for delegation. Please close the modal and try again');
                }
            })
            .catch(error => {
                dispatch({type: 'ErrorMessage', payload: error?.message || error || 'Error loading available steps for delegation. Please close the modal and try again'});
            })
            .finally(() => {
                dispatch({type: 'StopLoading'});
            });
    }, []);

    const formatDate = (date: Date) => {
        const d = new Date(date);
        let month = '' + (d.getMonth() + 1);
        let day = '' + d.getDate();
        const year = d.getFullYear();

        if (month.length < 2)
            month = '0' + month;
        if (day.length < 2)
            day = '0' + day;

        return [year, month, day].join('-');
    };

    const handleSave = () => {
        dispatch({type: 'StartSaving'});

        const startDate = formState.startDate ? formatDate(formState.startDate) : '';
        const endDate = formState.endDate ? formatDate(formState.endDate) : '';

        Flow.getInstance().saveDelegation(startDate, endDate, formState.showOutOfOffice, formState.workflowStepDelegates)
            .then((response: FlowFetchResponse) => {
                if (response.ok) {
                    setFormState({...initialFormState});
                    dispatch({type: 'Init'});
                    hide();
                    save();
                } else {
                    throw Error(response?.json?.message || 'Error saving Delegation. Please try again');
                }
            })
            .catch((error: any) => {
                dispatch({type: 'ErrorMessage', payload: error?.message || error || 'Error saving Delegation. Please try again'});
            })
            .finally(() => {
                dispatch({type: 'StopSaving'});
            })
    };

    const handleAddDelegation = (workflow: Workflow, delegateType: string, step: WorkflowStep, email: string) => {
        setFormState((state: NewDelegationFormState) => ({
            ...state,
            workflowStepDelegates: [
                ...state.workflowStepDelegates,
                {
                    workflow,
                    delegateType,
                    step,
                    email
                }
            ]
        }));
        dispatch({type: 'ClearSelectedStepAndDelegate'});
    };

    const handleRemoveDelegation = (index: number) => {
        setFormState((state: NewDelegationFormState) => ({
            ...state,
            workflowStepDelegates: [
                ...state.workflowStepDelegates.slice(0, index),
                ...state.workflowStepDelegates.slice(index + 1)
            ]
        }));
    };

    return (
        !show ?
            <div/>
            :
            <Modal size="lg">
                <div className="modal-header">
                    <h5 className="modal-title">Add New Delegation</h5>
                    <button type="button"
                            className="close"
                            aria-label="Close"
                            tabIndex={-1}
                            onClick={hide}
                    >
                        <span aria-hidden="true">×</span>
                    </button>
                </div>
                <div className="modal-body">
                    {modalState.errorMessage && <div className="alert alert-danger"><p>{modalState.errorMessage}</p></div>}
                    <div className="row">
                        <div className="col-3 form-group">
                            <label>Start Date</label>
                            <div>
                                <ReactDatepicker
                                    required={true}
                                    className="form-control"
                                    dateFormat={'MM/dd/yyyy'}
                                    onChange={(dateObj: Date) => {
                                        setFormState({
                                            ...formState,
                                            startDate: dateObj,
                                            endDate: formState.endDate || dateObj
                                        });
                                    }}
                                    selected={formState.startDate}
                                />
                            </div>
                        </div>
                        <div className="col-3 form-group">
                            <label>End Date</label>
                            <div>
                                <ReactDatepicker
                                    required={true}
                                    className="form-control"
                                    dateFormat={'MM/dd/yyyy'}
                                    onChange={(dateObj: Date) => {
                                        setFormState({
                                            ...formState,
                                            endDate: dateObj
                                        });
                                    }}
                                    selected={formState.endDate}
                                />
                            </div>
                        </div>
                        <div className="col-6">
                            <div className="form-check">
                                <input type="checkbox"
                                       className="form-check-input"
                                       id="delegation-show-ooo"
                                       checked={formState.showOutOfOffice}
                                       onChange={(e) => {
                                           setFormState({
                                               ...formState,
                                               showOutOfOffice: e.target.checked
                                           })
                                       }}
                                />
                                <label htmlFor="delegation-show-ooo">Show out of office?</label>
                                <p><small>Checking this option will show your out of office status next your name for
                                    any steps you are assigned to</small></p>
                            </div>
                        </div>
                    </div>
                    {!modalState.isLoading &&
                    <div className="form-check mb-2">
                        <input type="checkbox"
                               className="form-check-input"
                               id="delegation-select-workflow"
                               checked={modalState.showStepDelegation}
                               onChange={(e) => {
                                   if (!e.target.checked && formState.workflowStepDelegates.length > 0) {
                                       const confirmationMessage = 'You have 1 or more delegations assigned. If you continue, those will be cleared. Click "OK" to Continue.';
                                       if (confirm(confirmationMessage)) {
                                           dispatch({type: 'SetShowStepDelegation', payload: e.target.checked});
                                           dispatch({type: 'ClearSelectedStepAndDelegate'});
                                           dispatch({type: 'ClearSelectedWorkflow'});
                                       }
                                   } else {
                                       dispatch({type: 'SetShowStepDelegation', payload: e.target.checked});
                                       if (!e.target.checked) {
                                           dispatch({type: 'ClearSelectedStepAndDelegate'});
                                           dispatch({type: 'ClearSelectedWorkflow'});
                                       }
                                   }
                               }}
                               disabled={modalState.isLoading /* TODO: disable if steps have been delegated */}
                        />
                        <label htmlFor="delegation-select-workflow">Click here to assign a delegate for specific Workflows</label>
                    </div>
                    }
                    {modalState.showStepDelegation &&
                    <div className="form-group">
                        <label>Select a Workflow</label>
                        <small className="d-block">Not all Workflow Steps can be delegated. If you are an assignee for a Step in a Workflow and you do not see it listed, contact your workflow administrator for help.</small>
                        <small className="d-block"><strong>NOTE</strong> for ELIP workflows, any TOA approval <em>cannot</em> be delegated via Fletch. The online TOA must be updated instead.</small>
                        <ReactSelect
                            required={true}
                            onChange={(option) => {
                                dispatch({type: 'SelectWorkflow', payload: option});
                            }}
                            options={modalState.workflows?.sort((a: Workflow, b: Workflow) => a?.title ? a.title.localeCompare(b.title) : 0)?.map((workflow: Workflow) => ({
                                ...workflow,
                                label: workflow.title,
                                value: workflow.id
                            }))}
                            defaultValue={null}
                            value={modalState.selectedWorkflow}
                            placeholder="Select a Workflow"
                        />
                    </div>
                    }
                    {(modalState.selectedWorkflow || formState.workflowStepDelegates.length > 0) &&
                    <div className="form-group">
                        <p>There are 3 types of delegations:</p>
                        <ul>
                            <li><strong>Step</strong> - The workflow is configured to assign you to a specific step. This is the most common type of delegation.</li>
                            <li><strong>Manager</strong> - The workflows configured to pull a user's manager as an assignee.</li>
                            <li><strong>Manual</strong> - The assignee(s) for a step in the workflow are manually selected by a user.</li>
                        </ul>
                        <table className="table table-sm">
                            <thead>
                            <tr>
                                <th style={{width: '100px !important'}}/>
                                <th style={{width: '50%'}}>Workflow Step</th>
                                <th style={{width: '50%'}}>Delegate</th>
                                <th style={{width: '1px'}}></th>
                            </tr>
                            </thead>
                            <tbody>
                            {formState.workflowStepDelegates.map((workflowStepDelegate: WorkflowStepDelegate, idx: number) => (
                                <tr>
                                    <td/>
                                    <td>
                                        <div>{workflowStepDelegate.workflow.title}</div>
                                        <small className="d-block">
                                            <strong>
                                                {workflowStepDelegate.type === 'step' && workflowStepDelegate.step.name}
                                                {workflowStepDelegate.type === 'manager' && 'Manager'}
                                                {workflowStepDelegate.type === 'manual' && 'Manual'}
                                            </strong>
                                        </small>
                                    </td>
                                    <td>{workflowStepDelegate.email}</td>
                                    <td>
                                        <button type="button"
                                                className="btn btn-link"
                                                onClick={() => {
                                                    handleRemoveDelegation(idx);
                                                }}
                                        >Remove
                                        </button>
                                    </td>
                                </tr>
                            ))}
                            {modalState.selectedWorkflow &&
                            <tr>
                                <td>
                                    <ButtonDropdown
                                        label={modalState.selectedDelegateType ? modalState.selectedDelegateType.text : 'Select...'}
                                        options={
                                            [
                                                {value: 'step', label: 'Step', text: 'Step'},
                                                {value: 'manager', label: 'Manager', text: 'Manager'},
                                                {value: 'manual', label: 'Manual', text: 'Manual'},
                                            ]
                                                .filter((choice: Choice) => modalState.selectedWorkflow.steps.length > 0 || (modalState.selectedWorkflow.steps.length === 0 && ['manager', 'manual'].includes(choice.value)))
                                        }
                                        isActive={false}
                                        onSelect={(value, choice) => {
                                            dispatch({type: 'SetSelectedDelegationType', payload: choice});
                                        }}
                                        buttonClassName={'btn btn-block btn-link dropdown-toggle'}
                                    />
                                </td>
                                <td>
                                    {modalState?.selectedDelegateType?.value === 'step' ?
                                        <ReactSelect
                                            required={true}
                                            onChange={(option) => {
                                                dispatch({type: 'SelectWorkflowStep', payload: option});
                                            }}
                                            options={modalState.selectedWorkflow.steps.map((step: WorkflowStep) => ({
                                                ...step,
                                                label: step.name,
                                                value: step.id
                                            }))}
                                            defaultValue={null}
                                            value={modalState.selectedWorkflowStep}
                                            placeholder="Select a Step"
                                        />
                                        : <em className="d-block pt-1">Not applicable</em>
                                    }
                                </td>
                                <td>
                                    <input type="text"
                                           className="form-control"
                                           value={modalState.addDelegateValue}
                                           onChange={(e) => {
                                               dispatch({type: 'SetDelegateValue', payload: e.target.value});
                                           }}
                                           disabled={!modalState.selectedDelegateType}
                                    />
                                </td>
                                <td>
                                    <button type="button"
                                            className="btn btn-link"
                                            onClick={() => {
                                                handleAddDelegation(modalState.selectedWorkflow, modalState.selectedDelegateType.value, modalState.selectedWorkflowStep, modalState.addDelegateValue);
                                            }}
                                            disabled={!(
                                                (modalState.selectedWorkflowStep || ['manager', 'manual'].includes(modalState?.selectedDelegateType?.value)) &&
                                                /^\w+([\.-]?\w+)*@\w+([\.-]?\w+)*(\.\w{2,3})+$/.test(modalState.addDelegateValue)
                                            )}
                                    >Add
                                    </button>
                                </td>
                            </tr>
                            }
                            </tbody>
                        </table>
                    </div>
                    }
                </div>
                <div className="modal-footer">
                    <button type="button"
                            className="btn btn-secondary"
                            onClick={hide}
                            disabled={modalState.isSaving}
                    >Cancel
                    </button>
                    <ActivityButton
                        className="btn btn-primary"
                        isActive={modalState.isSaving}
                        onClick={() => {
                            handleSave();
                        }}
                    >Save</ActivityButton>
                </div>
            </Modal>
    )
}